/**
 * Email Score Utility
 *
 * Evaluates email quality scores from 3-step validation process
 * Returns color coding and user-friendly labels
 */

export interface EmailScoreConfig {
  color: string;        // Hex color for icon
  tailwindColor: string; // Tailwind class
  label: string;        // User-friendly label
  description: string;  // Detailed explanation for tooltip
}

/**
 * Get email score configuration based on score value
 *
 * Score ranges:
 * - 80-100: Green (Verified/Excellent)
 * - 50-79: Orange (Valid/Moderate)
 * - 0-49: Red (Risky/Poor)
 * - null/undefined: No color (Not checked)
 */
export const getEmailScoreConfig = (score: number | null | undefined): EmailScoreConfig | null => {
  // No score = email hasn't been validated yet
  if (score === null || score === undefined) {
    return null;
  }

  // Excellent - High deliverability
  if (score >= 80) {
    return {
      color: '#10B981',
      tailwindColor: 'text-emerald-500',
      label: 'Verified Email',
      description: `Email Score: ${score}/100 - Excellent deliverability. Passed all verification checks.`
    };
  }

  // Moderate - Acceptable but has some concerns
  if (score >= 50) {
    return {
      color: '#F59E0B',
      tailwindColor: 'text-amber-500',
      label: 'Valid Email',
      description: `Email Score: ${score}/100 - Moderate quality. Email is valid but may have minor issues.`
    };
  }

  // Poor - High risk of bounce
  return {
    color: '#EF4444',
    tailwindColor: 'text-red-500',
    label: 'Risky Email',
    description: `Email Score: ${score}/100 - Low quality. High risk of bounce or undeliverable.`
  };
};

/**
 * Get simple color for email icon (used in social icons)
 */
export const getEmailIconColor = (score: number | null | undefined): string | null => {
  const config = getEmailScoreConfig(score);
  return config ? config.color : null;
};

/**
 * Get tailwind class for email icon
 */
export const getEmailIconClass = (score: number | null | undefined): string => {
  const config = getEmailScoreConfig(score);
  return config ? config.tailwindColor : 'text-gray-400'; // Default gray if no score
};

/**
 * Check if email has been validated
 */
export const isEmailValidated = (score: number | null | undefined): boolean => {
  return score !== null && score !== undefined;
};

/**
 * Format email score for display
 */
export const formatEmailScore = (score: number | null | undefined): string => {
  if (score === null || score === undefined) {
    return 'Not validated';
  }
  return `${score}/100`;
};
